import React, { useCallback, type ReactNode } from 'react';
import { LinkButton } from '@atlaskit/button/new';
import { FormFooter, RequiredAsterisk } from '@atlaskit/form';
import { Box, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { CLICKUP_MAX_LISTS, CLICKUP_MULTI_LIST_INFO } from '../../../common/constants';
import type { SupportedThirdParty } from '../../../common/constants/import-type';
import { CROSS_FLOW } from '../../../common/constants/source';
import { PageDescription } from '../../../common/ui';
import { StepFooter, StepFooterStandard } from '../../../common/ui/step-footer';
import {
	redirectToProjectTemplatesScreen,
	useContentForThirdParty,
	useUrlForThirdParty,
} from '../../../common/utils';
import { useThirdPartyState } from '../../../controllers/import-state';
import { useSource } from '../../../controllers/import-state/selectors';
import {
	useAccessTokenModalState,
	useCancelImportModalState,
} from '../../../controllers/ui-state/selectors';
import { useImportTool } from '../../../controllers/utils';
import { AccountInfo } from './account-info';
import { EntitySelector } from './entity-selector';
import { EntitySelectorCheckbox } from './entity-selector-checkbox';
import { EntitySelectorWithPagination } from './entity-selector-with-pagination';
import messages from './messages';
import { useEntities } from './use-entities';
import { useFolders } from './use-folders';
import { useSpaces } from './use-spaces';
import { useSubfolders } from './use-subfolders';
import { useWorkspaces } from './use-workspaces';

export type EntityContentProps = {
	thirdParty: SupportedThirdParty;
	shouldShowPlaceholderIcons: boolean;
	shouldShowFolderSelector: boolean;
	hideWorkspaceSelector: boolean;
	shouldShowAccountInfoIfUsingPAT: boolean;
	shouldShowSpaceSelector: boolean;
	isMultipleEntitiesSupported: boolean;
	onReturn: () => void;
	onNext: () => void;
	hasSearchableEntities: boolean;
	onBlur?: () => void;
	onStatReset?: () => void;
};

export const EntityContent = ({
	thirdParty,
	shouldShowPlaceholderIcons,
	shouldShowFolderSelector,
	hideWorkspaceSelector,
	shouldShowAccountInfoIfUsingPAT,
	shouldShowSpaceSelector,
	onReturn,
	onNext,
	hasSearchableEntities,
	isMultipleEntitiesSupported,
	onBlur,
	onStatReset,
}: EntityContentProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [, { setCancelImportModalState }] = useCancelImportModalState();
	const [source] = useSource();
	const { formatMessage } = useIntl();
	const [, { setRequestAuthenticationToken: requestAuthenticationToken }] =
		useAccessTokenModalState();

	const [
		{
			workspaces,
			entities,
			selectedEntityId,
			selectedWorkspaceId,
			selectedEntities,
			selectedEntity,
		},
		{ setThirdPartyState },
	] = useThirdPartyState();

	const { description, entityScreenDescription, multiEntityInfo } =
		useContentForThirdParty(thirdParty);
	const tool = useImportTool(thirdParty);
	const { thirdPartyEntityDescriptionURL } = useUrlForThirdParty(thirdParty);

	const handleOnReturn = useCallback(() => {
		// Remove this when the new project create integration is implemented
		if (source === CROSS_FLOW) {
			redirectToProjectTemplatesScreen();
			return;
		}

		setThirdPartyState({
			selectedWorkspaceId: null,
			selectedSpaceId: null,
			selectedSpaceName: null,
			selectedFolderId: null,
			selectedSubfolderId: null,
			selectedEntities: null,
			selectedEntityId: null,
			selectedEntityName: null,
			selectedEntityStyle: null,
			entities: null,
			folders: null,
			workspaces: null,
			spaces: null,
			entityStats: null,
			members: null,
			siteName: null,
		});
		onReturn();
	}, [onReturn, setThirdPartyState, source]);

	const handleOnNext = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'thirdPartyEntity selected', {
			thirdParty,
			isStarredEntity: fg('jira_importer_third_party_state_refactor')
				? selectedEntity?.isStarred
				: entities?.find((entity) => entity.id === selectedEntityId)?.isStarred || false,
		});
		onNext();
	}, [
		createAnalyticsEvent,
		entities,
		onNext,
		selectedEntity?.isStarred,
		selectedEntityId,
		thirdParty,
	]);

	const handleOpenCancelImport = useCallback(() => {
		setCancelImportModalState({
			isCancelImportModalOpen: true,
			shouldCloseDrawerOnCancel: false,
			onCloseModalCallback: handleOnReturn,
		});
	}, [handleOnReturn, setCancelImportModalState]);

	const workspaceProps = useWorkspaces({
		thirdParty,
		shouldShowPlaceholderIcons,
		requestAuthenticationToken,
		hideWorkspaceSelector,
	});

	const spacesProps = useSpaces({
		thirdParty,
		shouldShowPlaceholderIcons,
		requestAuthenticationToken,
		shouldShowSpaceSelector,
	});

	const folderProps = useFolders({
		thirdParty,
		shouldShowPlaceholderIcons,
		requestAuthenticationToken,
		shouldShowFolderSelector,
		parentEntityId: shouldShowSpaceSelector ? spacesProps.selectedEntityId : selectedWorkspaceId,
	});

	const subfolderProps = useSubfolders({
		thirdParty,
		shouldShowPlaceholderIcons,
	});

	const entityProps = useEntities({
		thirdParty,
		shouldShowPlaceholderIcons,
		hasSearchableEntities,
		requestAuthenticationToken,
		hideWorkspaceSelector,
		parentEntityId: shouldShowSpaceSelector ? spacesProps.selectedEntityId : selectedWorkspaceId,
	});

	const isWorkspacesLoading = workspaceProps.isLoading;
	const isEntitiesLoading = entityProps.isLoading;

	const hasNoWorkspaces = !isWorkspacesLoading && workspaces && workspaces.length === 0;

	if (hasNoWorkspaces) {
		return (
			<>
				<EntitySelector {...workspaceProps} />
				{fg('enable-x2c-standardised-flow') ? (
					<StepFooterStandard
						alignRight
						onReturn={handleOnReturn}
						shouldDisableNext
						backButtonMessage={
							// Remove this when the new project create integration is implemented
							source === CROSS_FLOW ? messages.cancel : undefined
						}
					/>
				) : (
					<FormFooter align="start">
						<StepFooter
							onReturn={handleOnReturn}
							shouldRenderNextButton={false}
							backButtonMessage={
								// Remove this when the new project create integration is implemented
								source === CROSS_FLOW ? messages.cancel : undefined
							}
						/>
					</FormFooter>
				)}
			</>
		);
	}

	const BoardEntitySelectorComponent = hasSearchableEntities
		? EntitySelectorWithPagination
		: EntitySelector;

	const isWorkspaceIdSelected = hideWorkspaceSelector || selectedWorkspaceId !== null;
	const shouldShowBoardEntitySelector =
		!shouldShowSpaceSelector || spacesProps.selectedEntityId !== null;
	const showFolders =
		shouldShowFolderSelector &&
		!folderProps.isEmptyEntities &&
		(folderProps.selectedEntityId !== null || !entityProps.isEmptyEntities);
	const showSubfolders = showFolders && !subfolderProps.isEmptyEntities;
	const showSpaces = shouldShowSpaceSelector && isWorkspaceIdSelected;
	const isMultiEntitiesInvalid =
		!selectedEntities?.length || selectedEntities?.length > CLICKUP_MAX_LISTS;

	return (
		<>
			{ff('jira-business-to-software-project-imports') && entityScreenDescription ? (
				<PageDescription>
					{formatMessage(entityScreenDescription, {
						link: (chunks: ReactNode) => (
							<LinkButton
								appearance="link"
								spacing="none"
								href={thirdPartyEntityDescriptionURL}
								target="_blank"
							>
								{chunks}
							</LinkButton>
						),
					})}
				</PageDescription>
			) : (
				<PageDescription>{description}</PageDescription>
			)}
			{ff('jira-business-to-software-project-imports') ? (
				<PageDescription>
					{formatMessage(messages.requiredField)}
					<RequiredAsterisk />
				</PageDescription>
			) : (
				<PageDescription>{formatMessage(messages.requiredField)}</PageDescription>
			)}
			{tool?.requiresPersonalAccessToken === true && (
				<AccountInfo
					thirdParty={thirdParty}
					shouldShowAccountInfoIfUsingPAT={shouldShowAccountInfoIfUsingPAT}
					requestAuthenticationToken={requestAuthenticationToken}
				/>
			)}
			{!hideWorkspaceSelector && (
				<EntitySelector {...workspaceProps} isDisabled={isEntitiesLoading} isRequired />
			)}
			{isWorkspaceIdSelected && (
				<>
					{showSpaces && <EntitySelector {...spacesProps} isRequired />}
					{showFolders && <EntitySelector {...folderProps} />}
					{showSubfolders && <EntitySelector {...subfolderProps} />}
					{shouldShowBoardEntitySelector &&
						(isMultipleEntitiesSupported ? (
							<>
								<EntitySelectorCheckbox
									{...entityProps}
									onChange={entityProps.onChangeMultipleEntities}
									selectedEntities={entityProps.selectedEntities}
									onBlur={onBlur}
									onStatReset={onStatReset}
									isRequired
								/>
								{isMultipleEntitiesSupported && multiEntityInfo && (
									<Box xcss={infoSectionStyles}>
										<SectionMessage>
											{formatMessage(multiEntityInfo, {
												link: (chunks: ReactNode) => (
													<a href={CLICKUP_MULTI_LIST_INFO} target="_blank">
														{chunks}
													</a>
												),
											})}
										</SectionMessage>
									</Box>
								)}
							</>
						) : (
							<BoardEntitySelectorComponent {...entityProps} isRequired />
						))}
					{fg('enable-x2c-standardised-flow') ? (
						<StepFooterStandard
							alignRight
							onReturn={
								selectedWorkspaceId || selectedEntityId ? handleOpenCancelImport : handleOnReturn
							}
							onClickNext={handleOnNext}
							shouldDisableNext={
								isMultipleEntitiesSupported ? isMultiEntitiesInvalid : selectedEntityId === null
							}
							isLoading={isWorkspacesLoading || isEntitiesLoading}
							backButtonMessage={
								// Remove this when the new project create integration is implemented
								source === CROSS_FLOW ? messages.cancel : undefined
							}
						/>
					) : (
						<FormFooter align="end">
							<StepFooter
								onReturn={
									selectedWorkspaceId || selectedEntityId ? handleOpenCancelImport : handleOnReturn
								}
								onClickNext={handleOnNext}
								shouldDisableNext={
									isMultipleEntitiesSupported ? isMultiEntitiesInvalid : selectedEntityId === null
								}
								isLoading={isWorkspacesLoading || isEntitiesLoading}
								backButtonMessage={
									// Remove this when the new project create integration is implemented
									source === CROSS_FLOW ? messages.cancel : undefined
								}
							/>
						</FormFooter>
					)}
				</>
			)}
		</>
	);
};

export { EntitySelector, EntitySelectorWithPagination };

const infoSectionStyles = xcss({
	marginTop: 'space.300',
});
